/**
 * Avium brand palette — canonical hex source of truth.
 *
 * STAGING ONLY 2026-05-23: trying Option 10 "Turquoise Tile" from
 * avium-palette-options-v4.html for visual evaluation. Main branch
 * stays on Avium Indigo (#0F766E primary). Mirrored in
 * client/tailwind.config.js under the `brand-*` namespace.
 *
 * Rule: every brand color in the app should reference one of these
 * constants OR the matching `brand-*` Tailwind class.
 */

export const palette = {
  // Chrome — header, sidebar, primary CTAs
  primary:      '#0F766E',  // Teal 700 — chrome anchor
  primaryDark:  '#134E4A',  // Teal 900 — deepest
  primaryLight: '#CCFBF1',  // Teal 100 — selected states

  // Hero turquoise / AI pop — Avium AI Briefing, Insights tab, AI badges
  accent:       '#14B8A6',  // Teal 500 — the signature pop
  accentDark:   '#0D9488',  // Teal 600
  accentLight:  '#ECFEFB',  // Teal 50 — pale frost

  // Spark — data viz highlights, "new" badges, the logo "A" crossbar
  spark:        '#F97316',  // Orange 500
  sparkDark:    '#C2410C',  // Orange 700
  sparkLight:   '#FFF7ED',  // Orange 50

  // Knowledge Graph edges — risk-path edges use `spark`; this is the calm
  // structural-edge tone (also the legend swatch). Shared by the full graph
  // page and the ticket modal's In Context embed so they always match.
  graphStructure: '#5EEAD4', // Teal 300

  // Status colors
  success:      '#059669',  // Emerald 600
  successLight: '#D1FAE5',  // Emerald 100 — mirrors tailwind brand-success-light
  warning:      '#D97706',  // Amber 600
  danger:       '#DC2626',  // Red 600
  dangerLight:  '#FEE2E2',  // Red 100 — mirrors tailwind brand-danger-light
  info:         '#06B6D4',  // Cyan 500
  skyLight:     '#60A5FA',  // Blue 400 — pastel light-blue categorical accent (Effort×Impact "Big Bets"), distinct from the quick-win green

  // Surfaces + ink
  surface:      '#F8FAFC',  // Slate 50
  ink:          '#0F172A',  // Slate 900
  inkMuted:     '#475569',  // Slate 600
  border:       '#E2E8F0',  // Slate 200
} as const;

/**
 * Severity tokens — the single source for Avium Signals severity colors
 * (card rails, pills, glows). Extracted from The Brief's inline SEV constant
 * (2026-07-01) so every surface encoding severity reads one vocabulary.
 * `glow` is the translucent bloom behind critical/warning rails — the
 * "signal glow" treatment; translucent so it sits on any card background.
 */
export const severity = {
  critical: { base: '#EF4444', glow: 'rgba(239, 68, 68, 0.45)' },   // Red 500
  warning:  { base: '#F59E0B', glow: 'rgba(245, 158, 11, 0.38)' },  // Amber 500
  info:     { base: '#14B8A6', glow: 'rgba(20, 184, 166, 0.30)' },  // Teal 500
  positive: { base: '#10B981', glow: 'rgba(16, 185, 129, 0.30)' },  // Emerald 500
} as const;
export type SeverityToken = keyof typeof severity;

/**
 * Flight Deck night canvas — The Brief's Leadership cockpit commits to the
 * night-green stage in BOTH themes (the brand's demo-video ground; the old
 * hero band already established a dark surface on the light theme). One
 * source here so the deck, its stage and its drawer never drift. Node hues
 * validated with the dataviz palette checker against `canvas` (2026-07-11):
 * amber #D97706 / teal #0D9488 / coral #EA580C pass lightness, chroma,
 * CVD-separation and contrast; every node also carries a direct label.
 */
export const flightDeck = {
  canvas:       '#04110F',  // deck ground (matches the dark-mode surface token)
  panel:        '#0A231F',  // module panels (matches the dark-mode card token)
  panelEdge:    'rgba(20, 184, 166, 0.20)',   // module hairline
  ink:          '#E8F5F2',  // primary text on the canvas
  inkMuted:     '#9FBDB7',
  inkFaint:     '#6E8E88',
  headlineRisk: '#FCD34D',  // amber risk phrase in the verdict (existing hero tone)
  nodeSignal:   '#D97706',  // graph: signal (amber 600)
  nodeWork:     '#0D9488',  // graph: person / ticket (teal 600)
  nodeDate:     '#EA580C',  // graph: the at-risk sprint anchor (orange 600)
  edge:         'rgba(159, 189, 183, 0.28)',
  edgeHot:      '#FDBA74',  // the lit risk path (orange 300)
} as const;

/**
 * atlasDay — the C² "Daylight Atlas, tinted instruments" theme for The
 * Brief's Guided Atlas (approved direction 2026-07-11). The field is
 * daylight; window hierarchy is carried by WHISPER TINTS (teal = structure,
 * pale amber = risk strips); the night green survives only on the READING
 * layer (story card, node details) — the one magnetic dark object — and the
 * COMMAND layer (popups) wears its own ink-blue so "a modal is open" reads
 * from color alone. Discipline: tints stay pale on large fields, exactly two
 * dark surface classes, full chroma only on small marks.
 */
export const atlasDay = {
  // the field (L0)
  field:        'linear-gradient(180deg, #F8FBFA 0%, #EEF5F2 100%)',
  laneHeader:   '#8AA39D',
  // structure furniture (L1 · teal whisper tint)
  furniture:    '#F3FAF8',
  furnitureEdge:'#BFDED8',
  furnitureInk: '#0F766E',
  // risk strip (L1 · pale opaque amber — semantic, not decorative)
  riskStrip:    '#FFF7EA',
  riskStripEdge:'#F1C27D',
  riskStripInk: '#92400E',
  // the reading layer (L2 · the one magnetic dark object). Wears the SAME
  // midnight→teal→amber sweep as the route's home card (Brian, 2026-07-14:
  // the solid midnight panel "reads too dark" — supersedes the 2026-07-12
  // solid-midnight note for this layer; the midnight standard still anchors
  // the gradient's dark end and anchorBg). Stops stay a hair translucent so
  // the backdrop-blur glass survives. readingBase anchors tiny chrome (the
  // flyout notch) that would otherwise render the whole sweep in 14px;
  // readingInk/Sub/Faint are the text pairing for the teal/amber end — the
  // flightDeck inks were tuned for near-black and go muddy over #0F766E.
  reading:      'linear-gradient(125deg, rgba(4,17,15,.97) 0%, rgba(15,118,110,.95) 52%, rgba(181,83,27,.94) 100%)',
  // the TALL variant (node-details card): same hues, dark-weighted — a tall
  // box stretches the 52% teal stop across the whole content zone and the
  // dark-theme inks inside go muddy over #0F766E, so the sweep arrives as a
  // bottom flare instead and the reading zone stays deep.
  readingTall:  'linear-gradient(155deg, rgba(4,17,15,.97) 0%, rgba(6,44,39,.96) 50%, rgba(15,118,110,.94) 82%, rgba(181,83,27,.92) 100%)',
  readingBase:  'rgba(4, 17, 15, 0.97)',
  readingEdge:  'rgba(94, 234, 212, 0.35)',
  readingInk:   '#FFFFFF',
  readingSub:   'rgba(232, 245, 242, 0.85)',
  readingFaint: 'rgba(232, 245, 242, 0.6)',
  // the route's home card — the ORIGINAL Brief hero's green→amber gradient
  // (Brian, 2026-07-11: the Overview card wears the dark green + amber the
  // old hero band established), dark end grounded on the midnight standard
  routeHome:    'linear-gradient(125deg, #04110F 0%, #0F766E 52%, #B5531B 100%)',
  routeHomeEdge:'rgba(94, 234, 212, 0.5)',
  routeHomeInk: '#FFFFFF',
  routeHomeSub: 'rgba(232, 245, 242, 0.75)',
  // the command layer (L3 · popups — soft amber, Brian-approved 2026-07-11:
  // a LIGHT tinted panel over the scrim; elevation carried by shadow+scrim,
  // meaning carried by the tint)
  command:      'rgba(255, 247, 234, 0.98)',
  commandEdge:  '#F1C27D',
  commandInk:   '#7A4A12',
  commandMuted: '#B99B72',
  commandAccent:'#B45309',
  commandSelBg: '#FDEBCF',
  commandSelEdge:'#EBC48C',
  commandCta:   '#C2410C',
  commandScrim: 'rgba(26, 43, 41, 0.35)',
  // marks (small, full-chroma allowed)
  edge:         'rgba(20, 184, 166, 0.4)',
  edgeHot:      '#D97706',
  ink:          '#1A2B29',
  inkMuted:     '#4E6763',
  inkFaint:     '#8AA39D',
  // node fills/borders/text per identity (daylight variants)
  signalBg:     '#FEF3E2', signalEdge: '#D97706', signalInk: '#92400E',
  criticalBg:   '#FEE2E2', criticalEdge: '#EF4444', criticalInk: '#991B1B',
  underBg:      '#ECFDF9', underEdge: '#0D9488', underInk: '#0F766E',
  personRiskBg: '#FFF1E7', personRiskEdge: '#EA580C', personRiskInk: '#9A3412',
  personBg:     '#ECFDF9', personEdge: '#0F766E', personInk: '#0F766E',
  ticketBg:     '#FFFFFF', ticketEdge: '#CBDEDA', ticketRiskEdge: '#D97706', ticketRiskInk: '#92400E',
  epicBg:       '#F0FAF8', epicEdge: '#0D9488',
  anchorBg:     'linear-gradient(140deg, #0A231F, #04110F)',  // midnight card→surface (the standard dark)
  anchorEdge:   '#F59E0B',
  anchorGlow:   '0 10px 30px rgba(4, 17, 15, 0.40)',
  ringEdge:     'rgba(241, 194, 125, 0.9)',
} as const;

/**
 * What-If SCENARIO OVERLAY — the one new accent the capacity redesign's
 * forecast-ink discipline demands (docs/design/capacity-redesign-hifi.html
 * #state-c, approved 2026-07-18): a scenario is a hypothetical, never
 * confusable with real, observed data, so it wears a color no other severity
 * or brand token in this file uses. Locked to the hi-fi's own hex (Violet
 * 50/200/300/600/700/900) — do not substitute a brand teal here, the whole
 * point is that it reads as "not real yet".
 */
export const scenario = {
  ink:        '#4C1D95', // Violet 900 — the scenario bar's body text
  accent:     '#7C3AED', // Violet 600 — chip border/CTA/live chip ink
  accentDark: '#6D28D9', // Violet 700 — hover / Discard ink
  bg:         '#F5F3FF', // Violet 50 — bar fill + live-chip fill
  border:     '#DDD6FE', // Violet 200 — lever-chip border
  borderBar:  '#C4B5FD', // Violet 300 — the scenario bar's own border
} as const;

/**
 * Saturated chart palette — derives from the brand. Use for every
 * categorical chart (Recharts series, Workflow Sankey, distribution
 * pies, etc.). Replaces both the old pastel PHASE_COLORS and the
 * older LINE_COLORS Atlassian-blue set.
 */
export const chartPalette: readonly string[] = [
  palette.primary,   // #0F766E teal primary
  palette.success,   // #059669 emerald
  palette.spark,     // #F97316 orange
  palette.danger,    // #DC2626 red
  palette.accent,    // #14B8A6 teal accent
  palette.info,      // #06B6D4 cyan
] as const;
